import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import Card, { CardHeader } from "components/card";
import Skeleton from "components/skeleton/Skeleton";
import { formatDate, formatWeekday } from "utils/format";
import CardError from "./CardError";

const toDateStr = (date) => dayjs(date).format("YYYY-MM-DD");

const describeDay = (day, events) => {
  const services = events.filter((e) => e.type === "service").length;
  const holidays = events.filter((e) => e.type === "holiday").length;
  const parts = [formatDate(day)];
  if (services) parts.push(`${services} ${services === 1 ? "service" : "services"}`);
  if (holidays) parts.push(`${holidays} ${holidays === 1 ? "holiday" : "holidays"}`);
  return parts.join(", ");
};

const EventDot = ({ type, onToday }) => {
  const color =
    type === "service"
      ? onToday ? "bg-primary-fg" : "bg-info"
      : onToday ? "bg-primary-fg/60" : "bg-accent";
  return <span aria-hidden="true" className={`h-1.5 w-1.5 shrink-0 rounded-full ${color}`} />;
};

const ServiceCalendar = ({ data, loading, error, onRetry }) => {
  const todayStr = toDateStr(new Date());
  const [hoverDate, setHoverDate] = useState(null);
  const [pinnedDate, setPinnedDate] = useState(null);
  const activeDate = hoverDate ?? pinnedDate;

  const weekDays = useMemo(() => {
    const start = dayjs(todayStr);
    return Array.from({ length: 7 }, (_, i) => start.add(i, "day"));
  }, [todayStr]);

  const eventsByDate = useMemo(() => {
    const map = {};
    const add = (key, event) => {
      if (!map[key]) map[key] = [];
      map[key].push(event);
    };
    data?.services?.forEach((s) =>
      add(s.date, { type: "service", name: s.name, time: s.time, campus: s.campus })
    );
    data?.holidays?.forEach((h) => add(h.date, { type: "holiday", name: h.name }));
    return map;
  }, [data]);

  const activeEvents = activeDate ? eventsByDate[activeDate] || [] : [];

  const renderBody = () => {
    if (loading) {
      return (
        <div className="grid grid-cols-7 gap-1.5" aria-busy="true">
          {weekDays.map((day) => (
            <Skeleton key={day.valueOf()} className="h-16 rounded-control" />
          ))}
        </div>
      );
    }
    if (error) {
      return <CardError title="Couldn't Load This Week" error={error} onRetry={onRetry} />;
    }

    return (
      <>
        <ul className="grid grid-cols-7 gap-1.5">
          {weekDays.map((day) => {
            const dateStr = toDateStr(day);
            const isToday = dateStr === todayStr;
            const isActive = dateStr === activeDate;
            const events = eventsByDate[dateStr] || [];
            const hasService = events.some((e) => e.type === "service");
            const hasHoliday = events.some((e) => e.type === "holiday");
            const hasEvents = events.length > 0;

            const tone = isToday
              ? "bg-primary text-primary-fg"
              : isActive
              ? "bg-surface-subtle text-fg"
              : "bg-surface-raised text-fg";

            const content = (
              <>
                <span
                  className={`text-2xs font-medium uppercase ${
                    isToday ? "text-primary-fg/80" : "text-fg-muted"
                  }`}
                >
                  {formatWeekday(day)}
                </span>
                <span className="text-sm font-bold">{day.format("D")}</span>
                <span className="mt-1 flex h-2 gap-0.5">
                  {hasService && <EventDot type="service" onToday={isToday} />}
                  {hasHoliday && <EventDot type="holiday" onToday={isToday} />}
                </span>
              </>
            );

            const cellClass = `flex w-full flex-col items-center rounded-control px-1 py-2 transition-colors ${tone}`;

            return (
              <li key={dateStr} className="flex">
                {hasEvents ? (
                  <button
                    type="button"
                    aria-label={describeDay(day, events)}
                    aria-pressed={pinnedDate === dateStr}
                    aria-current={isToday ? "date" : undefined}
                    onClick={() => setPinnedDate((prev) => (prev === dateStr ? null : dateStr))}
                    onMouseEnter={() => setHoverDate(dateStr)}
                    onMouseLeave={() => setHoverDate(null)}
                    onFocus={() => setHoverDate(dateStr)}
                    onBlur={() => setHoverDate(null)}
                    className={`focus-ring ${cellClass}`}
                  >
                    {content}
                  </button>
                ) : (
                  <div
                    className={cellClass}
                    aria-label={describeDay(day, events)}
                    aria-current={isToday ? "date" : undefined}
                    role="group"
                  >
                    {content}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {activeEvents.length > 0 && (
          <ul className="flex flex-col gap-1.5 rounded-control bg-surface-raised px-3 py-2">
            {activeEvents.map((event, i) => (
              <li key={i} className="flex items-center gap-2 text-xs">
                <EventDot type={event.type} />
                {event.type === "service" ? (
                  <Link
                    to="/admin/attendance/services"
                    className="focus-ring truncate rounded-sm text-primary hover:underline dark:text-accent"
                  >
                    {event.name}
                    {event.time ? ` · ${event.time}` : ""}
                  </Link>
                ) : (
                  <span className="truncate text-fg">{event.name}</span>
                )}
                {event.campus && (
                  <span className="ml-auto shrink-0 text-2xs text-fg-muted">{event.campus}</span>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-center gap-4 text-xs text-fg-muted">
          <span className="flex items-center gap-1.5">
            <EventDot type="service" /> Service
          </span>
          <span className="flex items-center gap-1.5">
            <EventDot type="holiday" /> Holiday
          </span>
        </div>
      </>
    );
  };

  return (
    <Card padding="md" className="gap-4">
      <CardHeader
        title="This Week"
        description={`${formatDate(weekDays[0])} to ${formatDate(weekDays[6])}`}
      />
      {renderBody()}
    </Card>
  );
};

export default ServiceCalendar;
